/**
 * Deterministic, FICTIONAL leaderboard population for the offline demo backend.
 *
 * - ~400 players across all nine provinces (weighted roughly by population),
 *   in real South African towns (services/geo.ts), grades 1–12.
 * - Schools are invented ("Baobab High School", "Protea Primary",
 *   "Hoërskool Klipfontein", …) and carry a "demo-" id; any resemblance to a
 *   real school is accidental. Display names are the same fun nicknames the app
 *   suggests to players, never real names.
 * - Scores follow the game's design: RawScore grows with the difficulty index
 *   (so it correlates strongly with grade), while RatedScore is a handicap
 *   efficiency that depends on relative skill, not grade. RatedScore is
 *   computed by core's own formula over plausible answers, so demo scores stay
 *   on the real scale when scoring is re-tuned.
 * - Everything is a pure function of (seed, now): the same inputs give the same
 *   data, and dates spread over the 60 days before `now`.
 */
import {
  clamp,
  deriveSeed,
  getLevel,
  LEVEL_COUNT,
  ratedScoreForRun,
  ratingForLevel,
  Rng,
  SCORING,
  type Locale,
  type Outcome,
  type RatedAnswer,
} from '@dropstack/core';
import type { SchoolOption } from '../contracts.ts';
import type { BoardPlayer, BoardRun } from './backend/leaderboard.ts';
import { citiesFor, SA_PROVINCES, type SaProvinceCode } from './geo.ts';
import { suggestNickname } from './nicknames.ts';

export const DEMO_SEED = 20_260_923;
export const DEMO_PLAYER_COUNT = 400;
export const DEMO_HISTORY_DAYS = 60;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DemoSchool extends SchoolOption {
  /** Grades the school teaches (primary 1–7, high 8–12, combined 1–12). */
  minGrade: number;
  maxGrade: number;
}

export interface DemoPlayer extends BoardPlayer {
  locale: Locale;
  /** Epoch ms the fictional player "joined". */
  createdAt: number;
}

export interface DemoData {
  players: DemoPlayer[];
  schools: DemoSchool[];
  runs: BoardRun[];
}

export interface DemoOptions {
  /** Anchor for all dates (epoch ms). */
  now: number;
  seed?: number;
  playerCount?: number;
}

/** Approximate share of learners per province. */
const PROVINCE_WEIGHT: Readonly<Record<SaProvinceCode, number>> = {
  GP: 26, KZN: 19, WC: 12, EC: 11, LP: 10, MP: 8, NW: 7, FS: 5, NC: 2,
};

/** Share of Afrikaans-medium demo schools / Afrikaans-speaking demo players per province. */
const AFRIKAANS_SHARE: Readonly<Record<SaProvinceCode, number>> = {
  GP: 0.15, KZN: 0.05, WC: 0.4, EC: 0.12, LP: 0.08, MP: 0.12, NW: 0.25, FS: 0.35, NC: 0.55,
};

/**
 * Invented name stems. There are more stems than school slots (9 provinces ×
 * SCHOOL_SLOTS), and each is used once, so every demo school name is unique.
 */
const EN_STEMS = [
  'Baobab', 'Protea', 'Quiver Tree', 'Blue Crane', 'Kingfisher Bay', 'Sunbird Valley', 'Hornbill Heights',
  'Weaverbird', 'Meerkat Hill', 'Rainbow Ridge', 'Starfall', 'Moonflower', 'Waterberry', 'Fynbos Vale',
  'Dune Ridge', 'Rock Pool', 'Yellowwood Glen', 'Firefly Hill', 'Lantern Hill', 'Sandpiper Cove',
  'Honeybee Hill', 'Kestrel Ridge', 'Gemsbok Plains', 'Nightjar', 'Seashell Bay', 'Paper Kite', 'Otter Creek',
  'Sugarbird', 'Cloud Valley', 'Pebble Brook', 'Comet Crest', 'Dragonfly Dale', 'Stargazer', 'Pinwheel Park',
  'Rocket Ridge', 'Tortoise Trail', 'Lily Pond', 'Kite Hill', 'Galaxy Gardens', 'Windmill Vale', 'Moonbeam',
  'Coral Reef', 'Driftwood', 'Glowworm Glen', 'Bushbaby Bend', 'Lighthouse Point', 'Butterfly Meadow',
  'Fossil Ridge', 'Nebula Hill', 'Cosmos Corner',
] as const;

const AF_STEMS = [
  'Klipfontein', 'Glimwurmdal', 'Sterrekloof', 'Reënboogkruin', 'Windpompvlei', 'Sonneblomlaagte',
  'Kokerboomrand', 'Vuurvliegie', 'Maanskynvallei', 'Tortelduifrand', 'Swaeltjiesdal', 'Blouvalkkop',
  'Skoenlapperdal', 'Komeetkruin', 'Meerkatvlakte', 'Sterretjiesrand', 'Vlieërkop', 'Tolletjiesdal',
  'Wolkekrans', 'Seeskulpbaai',
] as const;

type SchoolKind = 'primary' | 'high' | 'combined';

const GRADES: Readonly<Record<SchoolKind, [number, number]>> = { primary: [1, 7], high: [8, 12], combined: [1, 12] };

const EN_NAMES: Readonly<Record<SchoolKind, readonly ((stem: string) => string)[]>> = {
  primary: [(s) => `${s} Primary`, (s) => `${s} Primary School`],
  high: [(s) => `${s} High School`, (s) => `${s} High`, (s) => `${s} College`],
  combined: [(s) => `${s} Academy`, (s) => `${s} Combined School`],
};

/** Afrikaans-medium schools are primary (laerskool) or high (hoërskool). */
const AF_NAMES: Readonly<Record<SchoolKind, readonly ((stem: string) => string)[]>> = {
  primary: [(s) => `Laerskool ${s}`],
  high: [(s) => `Hoërskool ${s}`],
  combined: [(s) => `Laerskool ${s}`, (s) => `Hoërskool ${s}`],
};

/** Per province: three primary, two high and one combined school, so every grade has a school. */
const SCHOOL_SLOTS: readonly SchoolKind[] = ['primary', 'primary', 'high', 'high', 'combined', 'primary'];

export function generateDemoData(options: DemoOptions): DemoData {
  const seed = options.seed ?? DEMO_SEED;
  const schools = generateSchools(new Rng(deriveSeed(seed, 1)));
  const { players, runs } = generatePlayers(
    new Rng(deriveSeed(seed, 2)),
    schools,
    options.now,
    options.playerCount ?? DEMO_PLAYER_COUNT,
    seed,
  );
  return { players, schools, runs };
}

function generateSchools(rng: Rng): DemoSchool[] {
  // Drawn without replacement across the whole country: no two demo schools share a name.
  const en: string[] = rng.shuffle(EN_STEMS);
  const af: string[] = rng.shuffle(AF_STEMS);
  return SA_PROVINCES.flatMap((province) => {
    const cities = citiesFor('ZA', province.code);
    // Earlier cities in the list are the bigger ones: favour them.
    const cityPool = cities.map((item, i) => ({ item, weight: 1 / (1 + 0.3 * i) }));
    return SCHOOL_SLOTS.map((kind, i): DemoSchool => {
      const wantAfrikaans = rng.chance(AFRIKAANS_SHARE[province.code]);
      const afrikaans = af.length > 0 && (wantAfrikaans || en.length === 0);
      const stem = (afrikaans ? af.pop() : en.pop()) ?? `${province.name.en} ${i + 1}`;
      const name = rng.pick(afrikaans ? AF_NAMES[kind] : EN_NAMES[kind])(stem);
      // An Afrikaans "combined" slot became a laerskool or hoërskool: take that school's grades.
      const effective: SchoolKind = afrikaans && kind === 'combined' ? (name.startsWith('Laer') ? 'primary' : 'high') : kind;
      const [minGrade, maxGrade] = GRADES[effective];
      return {
        id: `demo-school-${province.code.toLowerCase()}-${i + 1}`,
        name,
        city: rng.weighted(cityPool),
        province: province.code,
        country: 'ZA',
        minGrade,
        maxGrade,
      };
    });
  });
}

function generatePlayers(
  rng: Rng,
  schools: readonly DemoSchool[],
  now: number,
  count: number,
  seed: number,
): { players: DemoPlayer[]; runs: BoardRun[] } {
  const provinces = SA_PROVINCES.map((p) => ({ item: p.code, weight: PROVINCE_WEIGHT[p.code] }));
  const players: DemoPlayer[] = [];
  const runs: BoardRun[] = [];

  for (let i = 0; i < count; i++) {
    // Guarantee every province is represented, then follow the population weights.
    const province = SA_PROVINCES[i]?.code ?? rng.weighted(provinces);
    const grade = rng.int(1, 12);
    const options = schools.filter((s) => s.province === province && grade >= s.minGrade && grade <= s.maxGrade);
    const school = rng.pick(options.length > 0 ? options : schools.filter((s) => s.province === province));
    const afrikaansSchool = school.name.startsWith('Laerskool') || school.name.startsWith('Hoërskool');
    const locale: Locale = rng.chance(afrikaansSchool ? 0.85 : AFRIKAANS_SHARE[province] / 2) ? 'af' : 'en';
    const createdAt = Math.round(now - rng.next() * DEMO_HISTORY_DAYS * DAY_MS);
    const playerId = `demo-player-${String(i + 1).padStart(3, '0')}`;

    players.push({
      playerId,
      displayName: suggestNickname(locale, deriveSeed(seed, 10_000 + i)),
      grade,
      schoolId: school.id,
      schoolName: school.name,
      city: school.city,
      province,
      country: 'ZA',
      onboarded: true,
      leaderboardOptIn: rng.chance(0.93),
      locale,
      createdAt,
    });

    // Relative skill is independent of grade — that is what RatedScore measures.
    const skill = gaussian(rng);
    const baseLevel = clampInt(2 * grade - 1.5 + 1.2 * skill + (rng.next() - 0.5), 1, LEVEL_COUNT);
    const runCount = rng.int(1, 4);
    for (let r = 0; r < runCount; r++) {
      const at = Math.round(createdAt + rng.next() * (now - createdAt));
      const run = demoRun(rng, skill, baseLevel);
      const flagged = rng.chance(0.01);
      runs.push({
        runId: `demo-run-${i + 1}-${r + 1}`,
        playerId,
        // Flagged runs are the implausible ones: inflated scores that must never show up.
        raw: flagged ? run.raw * 4 : run.raw,
        rated: flagged ? run.rated * 4 : run.rated,
        levelReached: run.levelReached,
        at,
        flagged,
      });
    }
  }
  return { players, runs };
}

/**
 * RatedScore only reads response time relative to par, so every demo answer
 * shares one par time.
 */
const DEMO_PAR_MS = 10_000;

/** Plausible scores for one run, shaped like the real formulas (docs/GAME_DESIGN.md § 5). */
function demoRun(rng: Rng, skill: number, baseLevel: number): { raw: number; rated: number; levelReached: number } {
  const form = skill + 0.5 * gaussian(rng); // good and bad days
  const levelReached = clampInt(baseLevel + Math.max(0, form) * 1.2, 1, LEVEL_COUNT);
  const d = getLevel(levelReached).difficultyIndex;
  const answered = clampInt(34 + 9 * form + 8 * gaussian(rng), 12, 90);
  // Adaptive difficulty keeps everyone near 80% correct; skill shows mostly as speed.
  const accuracy = clamp(0.76 + 0.045 * form + 0.03 * gaussian(rng), 0.45, 0.95);
  const correct = Math.round(answered * accuracy);
  const wrong = answered - correct;
  const rated = ratedScoreForRun(demoAnswers(rng, form, correct, wrong, baseLevel, levelReached), ratingForLevel(levelReached));
  // RawScore: 100·D·(1 + time left)·streak per correct answer, plus layer breaks — scales with D.
  const perCorrect = SCORING.RAW_CORRECT * (1.35 + 0.15 * clamp(form, -1, 2) + 0.2 * rng.next());
  const layers = Math.min(correct, wrong + 3);
  const raw = Math.max(0, Math.round(d * (correct * perCorrect + SCORING.RAW_LAYER_BREAK * layers)));
  return { raw, rated, levelReached };
}

/**
 * The answers of one demo run, for core's RatedScore: spread over the top few
 * levels the run passed through (where an honest player spends their time),
 * answered faster than par in good form; misses are slow wrong answers or
 * timeouts.
 */
function demoAnswers(rng: Rng, form: number, correct: number, wrong: number, baseLevel: number, levelReached: number): RatedAnswer[] {
  const lowest = Math.max(baseLevel, levelReached - 2);
  const answer = (outcome: Outcome, tau: number): RatedAnswer => {
    const level = rng.int(lowest, levelReached);
    return {
      outcome,
      level,
      difficultyIndex: getLevel(level).difficultyIndex,
      parTimeMs: DEMO_PAR_MS,
      responseMs: Math.round(DEMO_PAR_MS * tau),
    };
  };
  const answers: RatedAnswer[] = [];
  for (let i = 0; i < correct; i++) answers.push(answer('correct', clamp(0.82 - 0.06 * form + 0.12 * gaussian(rng), 0.4, 1.8)));
  for (let i = 0; i < wrong; i++) {
    answers.push(rng.chance(0.4) ? answer('timeout', 2.5) : answer('wrong', clamp(1.2 + 0.3 * gaussian(rng), 0.5, 2)));
  }
  return answers;
}

/** Approximately standard-normal (Irwin–Hall with 4 uniforms). */
function gaussian(rng: Rng): number {
  return (rng.next() + rng.next() + rng.next() + rng.next() - 2) / Math.sqrt(4 / 12);
}

function clampInt(x: number, lo: number, hi: number): number {
  return clamp(Math.round(x), lo, hi);
}
